import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Flex,
  Typography,
  Button,
  Card,
  Table,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  Collapse,
  Row,
  Col,
  Spin,
  Space,
  Pagination,
  Alert,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ClearOutlined,
  FilterOutlined,
  DownOutlined,
  UpOutlined,
  BankOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  StarOutlined,
  ReloadOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import DashboardService from '../../service';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../hooks/useNotification';

const AccountsPage = () => {
  const { user: currentUser } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [stats, setStats] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [searchFilters, setSearchFilters] = useState({});
  const [isSearching, setIsSearching] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [availableStates, setAvailableStates] = useState([]);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState({
    auction: '',
    country: 'USA',
    account: '',
    password: '',
    buyer_number: '',
    status: 'on',
    type: 'master',
    auction_fee: 'low',
    fee: '1',
    bid_dealer_type: 'off',
    employee_id: '',
    office_id: '',
    note: '',
    states: []
  });

  const [userManagementOpen, setUserManagementOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountUsers, setAccountUsers] = useState([]);
  const [usersPagination, setUsersPagination] = useState({});
  const [usersLoading, setUsersLoading] = useState(false);
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [newUserId, setNewUserId] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [userSearchWaiting, setUserSearchWaiting] = useState(false);
  const userSearchDebounceRef = useRef();

  const [searchForm, setSearchForm] = useState({
    quickSearch: '',
    auction: '',
    country: '',
    status: '',
    type: '',
    auction_fee: '',
    min_users: '',
    max_users: ''
  });

  const debounceRef = useRef();
  const quickSearchInputRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        e.preventDefault();
        quickSearchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const filterAccountsByRole = (accountData) => {
    if (!currentUser) return [];
    if (currentUser.role === 'super_admin') return accountData;
    return accountData;
  };

  const loadAccounts = useCallback(async (page = 1, isNewSearch = false, forceSearchFilters = null) => {
    if (loading) return;

    setLoading(true);

    try {
      const currentSearchFilters = forceSearchFilters !== null ? forceSearchFilters : searchFilters;
      const shouldSearch = forceSearchFilters !== null
        ? Object.keys(forceSearchFilters).length > 0
        : (isSearching && Object.keys(searchFilters).length > 0);

      let data;
      if (shouldSearch) {
        data = await DashboardService.searchAuctionAccounts({
          ...currentSearchFilters,
          page,
          limit: 20,
          sortBy: 'created_at',
          sortOrder: 'desc'
        });
      } else {
        data = await DashboardService.getAuctionAccounts({
          page,
          limit: 20,
          sortBy: 'created_at',
          sortOrder: 'desc'
        });
      }

      let accountsList = [];
      let paginationData = null;

      if (data.success && data.accounts) {
        accountsList = Array.isArray(data.accounts) ? data.accounts : [];
        paginationData = data.pagination;
      } else if (Array.isArray(data)) {
        accountsList = data;
      } else if (data.accounts) {
        accountsList = Array.isArray(data.accounts) ? data.accounts : [];
        paginationData = data.pagination;
      } else {
        accountsList = Array.isArray(data) ? data : [];
      }

      const filteredAccounts = filterAccountsByRole(accountsList);
      setAccounts(filteredAccounts);

      if (paginationData) {
        setPagination(paginationData);
        setCurrentPage(paginationData.currentPage);
      } else {
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalAccounts: accountsList.length,
          hasNextPage: false,
          hasPrevPage: false,
          limit: accountsList.length
        });
        setCurrentPage(1);
      }
    } catch (err) {
      console.error('Error loading accounts:', err);
      showError(err.message || 'Failed to load accounts. Please try again later.');
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isSearching, searchFilters]);

  const loadStats = useCallback(async () => {
    try {
      const response = await DashboardService.getAuctionAccountStats();
      if (response.success && response.stats) {
        setStats(response.stats);
      } else if (response.stats) {
        setStats(response.stats);
      } else {
        setStats(response);
      }
    } catch (err) {
      console.error('Error loading stats:', err);
      setStats({
        total: 0,
        byStatus: { on: 0, off: 0, suspended: 0 },
        byAuction: { copart: 0, iaai: 0, manheim: 0 },
        byType: { master: 0, secondary: 0, backup: 0 },
        byCountry: { usa: 0, armenia: 0, georgia: 0 }
      });
    }
  }, []);

  const loadStates = useCallback(async () => {
    try {
      const statesData = await DashboardService.getAvailableStates();
      let states = [];
      if (statesData.states && statesData.states.all_states) {
        states = statesData.states.all_states;
      } else if (statesData.all_states) {
        states = statesData.all_states;
      } else if (Array.isArray(statesData)) {
        states = statesData;
      }
      const statesArray = Array.isArray(states) ? states : [];
      setAvailableStates(statesArray);

      if (!editingAccount && statesArray.length > 0) {
        setFormData(prev => {
          if (prev.states.length === 0) {
            return { ...prev, states: [...statesArray] };
          }
          return prev;
        });
      }
    } catch (err) {
      console.error('Error loading states:', err);
      setAvailableStates([]);
    }
  }, [editingAccount]);

  useEffect(() => {
    loadAccounts(1, true);
    loadStats();
    loadStates();
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (userSearchDebounceRef.current) clearTimeout(userSearchDebounceRef.current);
    };
  }, []);

  const debouncedQuickSearch = useCallback((searchTerm) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (searchTerm && searchTerm.trim() !== '') {
        const filters = { quickSearch: searchTerm.trim() };
        setSearchFilters(filters);
        setIsSearching(true);
        setCurrentPage(1);
        loadAccounts(1, true, filters);
      } else {
        clearSearch();
      }
    }, 300);
  }, [loadAccounts]);

  const handleQuickSearch = (value) => {
    setSearchForm(prev => ({ ...prev, quickSearch: value }));
    clearTimeout(debounceRef.current);
    if (value && value.trim() !== '') {
      debouncedQuickSearch(value.trim());
    } else {
      clearSearch();
    }
  };

  const handleAdvancedSearch = () => {
    clearTimeout(debounceRef.current);
    const filters = {};
    Object.entries(searchForm).forEach(([key, value]) => {
      if (key !== 'quickSearch' && value && value.toString().trim() !== '') {
        filters[key] = value.toString().trim();
      }
    });

    if (Object.keys(filters).length > 0) {
      setSearchFilters(filters);
      setIsSearching(true);
      setCurrentPage(1);
      loadAccounts(1, true, filters);
    } else {
      clearSearch();
    }
  };

  const clearSearch = () => {
    clearTimeout(debounceRef.current);
    setSearchFilters({});
    setIsSearching(false);
    setCurrentPage(1);
    setSearchForm({
      quickSearch: '',
      auction: '',
      country: '',
      status: '',
      type: '',
      auction_fee: '',
      min_users: '',
      max_users: ''
    });
    loadAccounts(1, true, {});
  };

  const handleUserSearch = async (searchTerm, reason) => {
    if (reason === 'reset' || reason === 'clear') {
      if (userSearchDebounceRef.current) {
        clearTimeout(userSearchDebounceRef.current);
        userSearchDebounceRef.current = null;
      }
      setUserSearchResults([]);
      setUserSearchLoading(false);
      setUserSearchWaiting(false);
      return;
    }

    if (userSearchDebounceRef.current) {
      clearTimeout(userSearchDebounceRef.current);
      userSearchDebounceRef.current = null;
    }

    if (!searchTerm || searchTerm.trim().length < 2) {
      setUserSearchResults([]);
      setUserSearchLoading(false);
      setUserSearchWaiting(false);
      return;
    }

    setUserSearchWaiting(true);
    userSearchDebounceRef.current = setTimeout(async () => {
      userSearchDebounceRef.current = null;
      try {
        setUserSearchWaiting(false);
        setUserSearchLoading(true);
        const response = await DashboardService.searchUsers({
          quickSearch: searchTerm.trim(),
          page: 1,
          limit: 50
        });
        setUserSearchResults(response?.users || []);
      } catch (error) {
        console.error('Error searching users:', error);
        setUserSearchResults([]);
      } finally {
        setUserSearchLoading(false);
      }
    }, 800);
  };

  const handleManageUsers = async (account) => {
    setSelectedAccount(account);
    setUserManagementOpen(true);
    await loadAccountUsers(account._id);
  };

  const loadAccountUsers = async (accountId, page = 1) => {
    setUsersLoading(true);
    try {
      const data = await DashboardService.getUsersForAccount(accountId, { page, limit: 20 });
      setAccountUsers(data.users);
      setUsersPagination(data.pagination);
    } catch (error) {
      console.error('Error loading account users:', error);
      showError('Failed to load users for this account.');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleAssignUser = async () => {
    if (!selectedUser || !selectedAccount) return;
    try {
      await DashboardService.assignUserToAccount(selectedAccount._id, selectedUser.user_id);
      setAddUserDialogOpen(false);
      setSelectedUser(null);
      setNewUserId('');
      setUserSearchResults([]);
      await loadAccountUsers(selectedAccount._id);
      await loadAccounts(currentPage);
      await loadStats();
      showSuccess('User assigned successfully');
    } catch (error) {
      console.error('Error assigning user:', error);
      showError(error.message || 'Failed to assign user to account.');
    }
  };

  const handleRemoveUser = async (userId) => {
    Modal.confirm({
      title: 'Remove User',
      content: 'Are you sure you want to remove this user from the auction account?',
      okText: 'Remove',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await DashboardService.removeUserFromAccount(selectedAccount._id, userId);
          await loadAccountUsers(selectedAccount._id);
          await loadAccounts(currentPage);
          await loadStats();
          showSuccess('User removed successfully');
        } catch (error) {
          console.error('Error removing user:', error);
          showError(error.message || 'Failed to remove user from account.');
        }
      }
    });
  };

  const handleFormChange = (field, value) => {
    setSearchForm(prev => ({ ...prev, [field]: value }));
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    loadAccounts(page);
  };

  const handleCreate = async () => {
    setEditingAccount(null);
    let currentStates = availableStates;
    if (availableStates.length === 0) {
      try {
        const statesData = await DashboardService.getAvailableStates();
        let states = [];
        if (statesData.states?.all_states) states = statesData.states.all_states;
        else if (statesData.all_states) states = statesData.all_states;
        else if (Array.isArray(statesData)) states = statesData;
        currentStates = Array.isArray(states) ? states : [];
        setAvailableStates(currentStates);
      } catch (err) {
        currentStates = [];
      }
    }
    setFormData({
      auction: '',
      country: 'USA',
      account: '',
      password: '',
      buyer_number: '',
      status: 'on',
      type: 'master',
      auction_fee: 'low',
      fee: '1',
      bid_dealer_type: 'off',
      employee_id: '',
      office_id: '',
      note: '',
      states: [...currentStates]
    });
    setIsFormOpen(true);
  };

  const handleEdit = async (account) => {
    setEditingAccount(account);
    let currentStates = availableStates;
    if (availableStates.length === 0) {
      try {
        const statesData = await DashboardService.getAvailableStates();
        let states = [];
        if (statesData.states?.all_states) states = statesData.states.all_states;
        else if (statesData.all_states) states = statesData.all_states;
        else if (Array.isArray(statesData)) states = statesData;
        currentStates = Array.isArray(states) ? states : [];
        setAvailableStates(currentStates);
      } catch (err) {
        currentStates = [];
      }
    }
    const accountStates = Array.isArray(account.states) ? account.states : [];
    setFormData({
      auction: account.auction || '',
      country: account.country || 'USA',
      account: account.account || '',
      password: account.password || '',
      buyer_number: account.buyer_number || '',
      status: account.status || 'on',
      type: account.type || 'master',
      auction_fee: account.auction_fee || 'low',
      fee: account.fee || '1',
      bid_dealer_type: account.bid_dealer_type || 'off',
      employee_id: account.employee_id || '',
      office_id: account.office_id || '',
      note: account.note || '',
      states: accountStates
    });
    setIsFormOpen(true);
  };

  const handleDelete = async (id, accountName) => {
    Modal.confirm({
      title: 'Delete Account',
      content: `Are you sure you want to delete account "${accountName}"?`,
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await DashboardService.deleteAuctionAccount(id);
          loadAccounts(currentPage);
          loadStats();
          showSuccess('Account deleted successfully');
        } catch (error) {
          console.error('Error deleting account:', error);
          showError('Failed to delete account. Please try again.');
        }
      }
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!formData.auction || !formData.auction.trim()) {
        showError('Auction type is required');
        setLoading(false);
        return;
      }
      if (!formData.account || !formData.account.trim()) {
        showError('Account username is required');
        setLoading(false);
        return;
      }
      if (!editingAccount && (!formData.password || !formData.password.trim())) {
        showError('Password is required for new accounts');
        setLoading(false);
        return;
      }
      if (formData.states.length === 0 && availableStates.length === 0) {
        showError('At least one state must be selected');
        setLoading(false);
        return;
      }

      const submissionData = {
        ...formData,
        states: formData.states.length === 0 ? availableStates : formData.states,
        employee_id: formData.employee_id ? parseInt(formData.employee_id) : undefined,
        office_id: formData.office_id ? parseInt(formData.office_id) : undefined
      };

      if (editingAccount && (!submissionData.password || !submissionData.password.trim())) {
        delete submissionData.password;
      }

      if (editingAccount) {
        await DashboardService.updateAuctionAccount(editingAccount._id, submissionData);
      } else {
        await DashboardService.createAuctionAccount(submissionData);
      }

      setIsFormOpen(false);
      setEditingAccount(null);
      loadAccounts(currentPage);
      loadStats();
      showSuccess(editingAccount ? 'Account updated successfully' : 'Account created successfully');
    } catch (error) {
      console.error('Error saving account:', error);
      showError(error.message || 'Failed to save account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStateChange = (value) => {
    const valueArray = Array.isArray(value) ? value : (typeof value === 'string' ? value.split(',') : []);
    setFormData(prev => ({ ...prev, states: valueArray }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'on': return 'success';
      case 'off': return 'default';
      case 'suspended': return 'error';
      default: return 'default';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'master': return 'blue';
      case 'secondary': return 'cyan';
      case 'backup': return 'orange';
      default: return 'default';
    }
  };

  const getCountryFlag = (country) => {
    const flags = { 'USA': '🇺🇸', 'Armenia': '🇦🇲', 'Georgia': '🇬🇪' };
    return flags[country] || '';
  };

  const getActiveFilterCount = () => {
    return Object.values(searchFilters).filter(v => v && v.trim() !== '').length;
  };

  const userOptionsForSelect = (() => {
    const list = selectedUser && !userSearchResults.some(u => u.user_id === selectedUser.user_id)
      ? [selectedUser, ...userSearchResults] : userSearchResults;
    return list.map(u => ({
      value: u.user_id,
      label: `${u.name || u.user_name || 'Unknown'} | ${u.user_name || ''} | ${u.email || ''} | ID: ${u.user_id || ''}`,
      data: u
    }));
  })();

  const statCards = [
    { title: 'Total Accounts', value: stats?.total || 0, icon: <BankOutlined />, color: '#1e3a5f' },
    { title: 'Active Accounts', value: stats?.byStatus?.on || 0, icon: <CheckCircleOutlined />, color: '#52c41a' },
    { title: 'Copart Accounts', value: stats?.byAuction?.copart || 0, icon: <BankOutlined />, color: '#fa8c16' },
    { title: 'IAAI Accounts', value: stats?.byAuction?.iaai || 0, icon: <BankOutlined />, color: '#d32f2f' },
    { title: 'IAAI Canada', value: stats?.byAuction?.iaai_canada || 0, icon: <BankOutlined />, color: '#d32f2f' },
    { title: 'Manheim', value: stats?.byAuction?.manheim || 0, icon: <BankOutlined />, color: '#ed6c02' },
    { title: 'ACV', value: stats?.byAuction?.acv || 0, icon: <BankOutlined />, color: '#722ed1' }
  ];

  const columns = [
    { title: 'Auction', dataIndex: 'auction', key: 'auction', render: (v) => <Tag>{v}</Tag> },
    { title: 'Country', dataIndex: 'country', key: 'country', render: (v) => `${getCountryFlag(v)} ${v || 'N/A'}` },
    { title: 'Account', dataIndex: 'account', key: 'account' },
    { title: 'Buyer Number', dataIndex: 'buyer_number', key: 'buyer_number' },
    { title: 'Status', dataIndex: 'status', key: 'status', render: (v) => <Tag color={getStatusColor(v)}>{v}</Tag> },
    { title: 'Type', dataIndex: 'type', key: 'type', render: (v) => <Tag color={getTypeColor(v)}>{v}</Tag> },
    { title: 'States', key: 'states', render: (_, r) => `${r.states?.length || 0} states` },
    {
      title: 'Users',
      key: 'users',
      render: (_, account) => (
        <Button type="link" size="small" onClick={() => handleManageUsers(account)}>
          {account.userCount || 0} users
        </Button>
      )
    },
    { title: 'Fee', dataIndex: 'auction_fee', key: 'auction_fee' },
    { title: 'Created', dataIndex: 'created_at', key: 'created_at', render: (v) => v ? new Date(v).toLocaleDateString() : 'N/A' },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, account) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(account)} title="Edit Account" disabled={currentUser?.role === 'subdealer'} />
          <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDelete(account._id, account.account)} title="Delete Account" disabled={currentUser?.role === 'subdealer'} />
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 20 }}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 24 }}>
        <Typography.Title level={3} style={{ margin: 0, color: '#1e3a5f' }}>
          Auction Accounts Management
          {isSearching && (
            <Tag color="blue" closable onClose={clearSearch} style={{ marginLeft: 8 }}>
              {getActiveFilterCount()} filter(s) active
            </Tag>
          )}
        </Typography.Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => { loadAccounts(currentPage); loadStats(); }} title="Refresh" />
          {currentUser?.role !== 'subdealer' && (
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              Add Account
            </Button>
          )}
        </Space>
      </Flex>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {statCards.map((stat, index) => (
          <Col xs={24} sm={12} md={8} lg={6} key={index}>
            <Card size="small">
              <Flex align="center" gap={8}>
                <span style={{ color: stat.color, fontSize: 20 }}>{stat.icon}</span>
                <div>
                  <Typography.Title level={4} style={{ margin: 0 }}>{stat.value}</Typography.Title>
                  <Typography.Text type="secondary">{stat.title}</Typography.Text>
                </div>
              </Flex>
            </Card>
          </Col>
        ))}
      </Row>

      <Input
        ref={quickSearchInputRef}
        placeholder="Search by Account, Buyer Number, States..."
        prefix={<SearchOutlined />}
        value={searchForm.quickSearch}
        onChange={(e) => handleQuickSearch(e.target.value)}
        suffix={
          searchForm.quickSearch ? (
            <Button type="text" size="small" icon={<ClearOutlined />} onClick={() => handleQuickSearch('')} />
          ) : (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>Ctrl+F / ⌘F</Typography.Text>
          )
        }
        style={{ marginBottom: 16 }}
      />

      <Collapse
        activeKey={showAdvancedFilters ? ['1'] : []}
        onChange={(keys) => setShowAdvancedFilters(keys.length > 0)}
        style={{ marginBottom: 16 }}
      >
        <Collapse.Panel header={<><FilterOutlined /> Advanced Filters</>} key="1">
          <Card size="small">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8}>
                <Select placeholder="Auction Type" allowClear style={{ width: '100%' }} value={searchForm.auction || undefined} onChange={(v) => handleFormChange('auction', v)} options={[
                  { value: '', label: 'All' },
                  { value: 'Copart', label: 'Copart' },
                  { value: 'IAAI', label: 'IAAI' },
                  { value: 'IAAI_Canada', label: 'IAAI Canada' },
                  { value: 'Manheim', label: 'Manheim' },
                  { value: 'ACV', label: 'ACV' }
                ]} />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Select placeholder="Country" allowClear style={{ width: '100%' }} value={searchForm.country || undefined} onChange={(v) => handleFormChange('country', v)} options={[
                  { value: '', label: 'All Countries' },
                  { value: 'USA', label: '🇺🇸 USA' },
                  { value: 'Armenia', label: '🇦🇲 Armenia' },
                  { value: 'Georgia', label: '🇬🇪 Georgia' }
                ]} />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Select placeholder="Status" allowClear style={{ width: '100%' }} value={searchForm.status || undefined} onChange={(v) => handleFormChange('status', v)} options={[
                  { value: '', label: 'All' },
                  { value: 'on', label: 'Active' },
                  { value: 'off', label: 'Inactive' },
                  { value: 'suspended', label: 'Suspended' }
                ]} />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Select placeholder="Type" allowClear style={{ width: '100%' }} value={searchForm.type || undefined} onChange={(v) => handleFormChange('type', v)} options={[
                  { value: '', label: 'All' },
                  { value: 'master', label: 'Master' },
                  { value: 'secondary', label: 'Secondary' },
                  { value: 'backup', label: 'Backup' }
                ]} />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Input placeholder="Min Users" type="number" value={searchForm.min_users} onChange={(e) => handleFormChange('min_users', e.target.value)} />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Input placeholder="Max Users" type="number" value={searchForm.max_users} onChange={(e) => handleFormChange('max_users', e.target.value)} />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Select placeholder="Auction Fee" allowClear style={{ width: '100%' }} value={searchForm.auction_fee || undefined} onChange={(v) => handleFormChange('auction_fee', v)} options={[
                  { value: '', label: 'All' },
                  { value: 'low', label: 'Low' },
                  { value: 'standard', label: 'Standard' },
                  { value: 'high', label: 'High' }
                ]} />
              </Col>
            </Row>
            <Space style={{ marginTop: 16 }}>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleAdvancedSearch} loading={loading}>
                {loading ? 'Searching...' : 'Apply Filters'}
              </Button>
              <Button onClick={clearSearch} disabled={loading}>Clear All</Button>
            </Space>
          </Card>
        </Collapse.Panel>
      </Collapse>

      {initialLoading ? (
        <Flex justify="center" align="center" style={{ padding: 48 }}>
          <Spin size="large" />
          <Typography.Text style={{ marginLeft: 16 }}>Loading accounts...</Typography.Text>
        </Flex>
      ) : (
        <>
          <Card>
            <Table
              columns={columns}
              dataSource={accounts}
              rowKey="_id"
              loading={loading && !initialLoading}
              pagination={false}
              locale={{ emptyText: isSearching ? 'No accounts found matching your search criteria.' : 'No accounts found.' }}
            />
          </Card>

          {pagination.totalPages > 1 && (
            <Flex justify="center" align="center" style={{ marginTop: 24 }} gap={16}>
              <Pagination
                current={currentPage}
                total={pagination.totalAccounts || 0}
                pageSize={20}
                onChange={handlePageChange}
                showSizeChanger={false}
                showTotal={(total) => `Showing ${accounts.length} of ${total} accounts`}
              />
            </Flex>
          )}
        </>
      )}

      <Modal
        title={editingAccount ? 'Edit Auction Account' : 'Create New Auction Account'}
        open={isFormOpen}
        onCancel={() => setIsFormOpen(false)}
        footer={null}
        width={720}
      >
        <form onSubmit={handleFormSubmit}>
          <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                <Form.Item label="Auction Type" required>
                  <Select value={formData.auction} onChange={(v) => setFormData({ ...formData, auction: v })} style={{ width: '100%' }} options={[
                    { value: 'Copart', label: 'Copart' },
                    { value: 'IAAI', label: 'IAAI' },
                    { value: 'IAAI_Canada', label: 'IAAI Canada' },
                    { value: 'Manheim', label: 'Manheim' },
                    { value: 'ACV', label: 'ACV' }
                  ]} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Country" required>
                  <Select value={formData.country} onChange={(v) => setFormData({ ...formData, country: v })} style={{ width: '100%' }} options={[
                    { value: 'USA', label: '🇺🇸 USA' },
                    { value: 'Armenia', label: '🇦🇲 Armenia' },
                    { value: 'Georgia', label: '🇬🇪 Georgia' }
                  ]} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Account Type">
                  <Select value={formData.type} onChange={(v) => setFormData({ ...formData, type: v })} style={{ width: '100%' }} options={[
                    { value: 'master', label: 'Master' },
                    { value: 'secondary', label: 'Secondary' },
                    { value: 'backup', label: 'Backup' }
                  ]} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Account Username/Email" required>
                  <Input value={formData.account} onChange={(e) => setFormData({ ...formData, account: e.target.value })} placeholder="Enter account username or email" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label={editingAccount ? 'Password (leave blank to keep current)' : 'Password'} required={!editingAccount}>
                  <Input.Password value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder={editingAccount ? 'Leave blank to keep current' : 'Enter password'} />
                </Form.Item>
              </Col>
              {!['Manheim', 'ACV'].includes(formData.auction) && (
                <Col xs={24} md={12}>
                  <Form.Item label="Buyer Number">
                    <Input value={formData.buyer_number} onChange={(e) => setFormData({ ...formData, buyer_number: e.target.value })} placeholder="Enter buyer number" />
                  </Form.Item>
                </Col>
              )}
              <Col xs={24} md={12}>
                <Form.Item label="Status">
                  <Select value={formData.status} onChange={(v) => setFormData({ ...formData, status: v })} style={{ width: '100%' }} options={[
                    { value: 'on', label: 'On (Active)' },
                    { value: 'off', label: 'Off (Inactive)' },
                    { value: 'suspended', label: 'Suspended' }
                  ]} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Auction Fee">
                  <Select value={formData.auction_fee} onChange={(v) => setFormData({ ...formData, auction_fee: v })} style={{ width: '100%' }} options={[
                    { value: 'low', label: 'Low' },
                    { value: 'standard', label: 'Standard' },
                    { value: 'high', label: 'High' }
                  ]} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Fee">
                  <Input value={formData.fee} onChange={(e) => setFormData({ ...formData, fee: e.target.value })} placeholder="Enter fee amount" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Bid Dealer Type">
                  <Select value={formData.bid_dealer_type} onChange={(v) => setFormData({ ...formData, bid_dealer_type: v })} style={{ width: '100%' }} options={[
                    { value: 'off', label: 'Off' },
                    { value: 'on', label: 'On' }
                  ]} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Employee ID">
                  <Input type="number" value={formData.employee_id} onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })} placeholder="Optional employee ID" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Office ID">
                  <Input type="number" value={formData.office_id} onChange={(e) => setFormData({ ...formData, office_id: e.target.value })} placeholder="Optional office ID" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Flex gap={8} align="center" style={{ marginBottom: 8 }}>
                  <Typography.Text strong>Available States:</Typography.Text>
                  <Button size="small" onClick={() => setFormData(prev => ({ ...prev, states: [...availableStates] }))} disabled={availableStates.length === 0}>Select All</Button>
                  <Button size="small" danger onClick={() => setFormData(prev => ({ ...prev, states: [] }))}>Clear</Button>
                  <Tag color={formData.states.length === availableStates.length ? 'success' : 'default'}>{formData.states.length}/{availableStates.length}</Tag>
                </Flex>
                <Form.Item>
                  <Select
                    mode="multiple"
                    placeholder="Select States"
                    value={Array.isArray(formData.states) ? formData.states : []}
                    onChange={handleStateChange}
                    style={{ width: '100%' }}
                    maxTagCount="responsive"
                    options={availableStates.map(s => ({ value: s, label: s }))}
                    notFoundContent={availableStates.length === 0 ? 'Loading states...' : null}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label="Note">
                  <Input.TextArea rows={3} value={formData.note} onChange={(e) => setFormData({ ...formData, note: e.target.value })} />
                </Form.Item>
              </Col>
            </Row>
          </div>
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
            <Space>
              <Button onClick={() => setIsFormOpen(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {loading ? 'Saving...' : (editingAccount ? 'Update' : 'Create')}
              </Button>
            </Space>
          </div>
        </form>
      </Modal>

      <Modal
        title={
          <div>
            <div>Manage Users - {selectedAccount?.auction} Account: {selectedAccount?.account}</div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>{usersPagination.totalUsers || 0} users assigned to this account</Typography.Text>
          </div>
        }
        open={userManagementOpen}
        onCancel={() => setUserManagementOpen(false)}
        footer={[<Button key="close" onClick={() => setUserManagementOpen(false)}>Close</Button>]}
        width={720}
      >
        <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
          <Typography.Text strong>Users in this account</Typography.Text>
          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setAddUserDialogOpen(true)}>Add User</Button>
        </Flex>

        {usersLoading ? (
          <Flex justify="center" style={{ padding: 32 }}><Spin /></Flex>
        ) : (
          <Table
            columns={[
              { title: 'User ID', dataIndex: 'user_id', key: 'user_id' },
              { title: 'Name', dataIndex: 'name', key: 'name' },
              { title: 'Username', dataIndex: 'user_name', key: 'user_name' },
              {
                title: 'Status',
                key: 'status',
                render: (_, u) => {
                  const isActive = (selectedAccount?.auction === 'Copart' && u.copart_on) ||
                    (selectedAccount?.auction === 'IAAI' && u.iaai_on) ||
                    (selectedAccount?.auction === 'IAAI_Canada' && u.iaai_canada_on) ||
                    (selectedAccount?.auction === 'Manheim' && u.manheim_on) ||
                    (selectedAccount?.auction === 'ACV' && u.acv_on);
                  return <Tag color={isActive ? 'success' : 'default'}>{isActive ? 'Active' : 'Inactive'}</Tag>;
                }
              },
              {
                title: 'Actions',
                key: 'actions',
                render: (_, u) => (
                  <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleRemoveUser(u.user_id)} title="Remove User" />
                )
              }
            ]}
            dataSource={accountUsers}
            rowKey="user_id"
            pagination={false}
            locale={{ emptyText: 'No users assigned to this account' }}
          />
        )}

        {usersPagination.totalPages > 1 && (
          <Flex justify="center" vertical align="center" gap={8} style={{ marginTop: 16 }}>
            <Pagination
              size="small"
              current={usersPagination.currentPage || 1}
              total={usersPagination.totalUsers || 0}
              pageSize={20}
              onChange={(page) => loadAccountUsers(selectedAccount._id, page)}
              showSizeChanger={false}
            />
            <Typography.Text type="secondary">Showing {accountUsers.length} of {usersPagination.totalUsers || 0} users</Typography.Text>
          </Flex>
        )}
      </Modal>

      <Modal
        title={`Add User to ${selectedAccount?.auction} Account`}
        open={addUserDialogOpen}
        onCancel={() => {
          if (userSearchDebounceRef.current) {
            clearTimeout(userSearchDebounceRef.current);
            userSearchDebounceRef.current = null;
          }
          setAddUserDialogOpen(false);
          setSelectedUser(null);
          setNewUserId('');
          setUserSearchResults([]);
          setUserSearchLoading(false);
          setUserSearchWaiting(false);
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            if (userSearchDebounceRef.current) clearTimeout(userSearchDebounceRef.current);
            setAddUserDialogOpen(false);
            setSelectedUser(null);
            setNewUserId('');
            setUserSearchResults([]);
          }}>Cancel</Button>,
          <Button key="add" type="primary" onClick={handleAssignUser} disabled={!selectedUser}>Add User</Button>
        ]}
        width={480}
      >
        <Select
          showSearch
          filterOption={false}
          placeholder="Type username or email to search..."
          value={selectedUser?.user_id || undefined}
          onChange={(value, option) => {
            const u = option?.data;
            setSelectedUser(u || null);
            setNewUserId(u?.user_id || '');
          }}
          onSearch={(v) => handleUserSearch(v, 'input')}
          onClear={() => {
            setSelectedUser(null);
            setNewUserId('');
            setUserSearchResults([]);
          }}
          loading={userSearchLoading || userSearchWaiting}
          notFoundContent={userSearchWaiting ? 'Searching...' : 'Type at least 2 characters'}
          style={{ width: '100%' }}
          allowClear
          optionRender={(opt) => (
            <div>
              <div style={{ fontWeight: 500 }}>{opt.data?.name || opt.data?.user_name || 'Unknown'}</div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {opt.data?.user_name && `Username: ${opt.data.user_name}`}
                {opt.data?.user_name && opt.data?.email && ' • '}
                {opt.data?.email && `Email: ${opt.data.email}`}
                {(opt.data?.user_name || opt.data?.email) && opt.data?.user_id && ' • '}
                {opt.data?.user_id && `ID: ${opt.data.user_id}`}
              </Typography.Text>
            </div>
          )}
          options={userOptionsForSelect}
        />
      </Modal>
    </div>
  );
};

export default AccountsPage;
